import { useCallback } from 'react';

import { twMerge } from 'tailwind-merge';

import { Text } from '@/_components';
import { type AdvanceFilter, FilterTrigger } from '@/_containers';
import type { SearchingProps } from '@/_containers/data-table/toolbar/use-api-controller';
import { SectionHeading } from '@/_containers/headers';
import { DebouncedInput } from '@/_containers/inputs';

import { BodyScrollVirtualizedList } from './body-scroll-virtualized-list';
import { TicketListItem } from './components';
import { useSupportTicketsInfinite } from './use-support-tickets-infinite';

import type { SupportFilterSchema } from '../shared/filters';
import type { SupportModule, SupportTicketSummary } from '../shared/schemas/support-query-schema';

type SupportTicketsInfiniteListProps = {
  module: SupportModule;
  moduleId: string;
  freshdeskId: string;
  sort?: string;
  per_page?: number;
  filters?: string;
  searching: SearchingProps;
  advFiltersState: AdvanceFilter<SupportFilterSchema>[];
  isAllListPage?: boolean;
};

export function SupportTicketsInfiniteList({
  module,
  moduleId,
  freshdeskId,
  sort = '-updated_at',
  per_page = 25,
  filters = '',
  searching,
  advFiltersState,
  isAllListPage = false,
}: SupportTicketsInfiniteListProps) {
  const { tickets, totalCount, hasMore, isLoading, fetchMore } = useSupportTicketsInfinite({
    module,
    freshdeskId,
    sort,
    per_page,
    filters,
    search: searching.search_str,
  });

  const renderItem = useCallback(
    (ticket: SupportTicketSummary) => (
      <div className="w-full">
        <TicketListItem ticket={ticket} module={module} moduleId={moduleId} />
      </div>
    ),
    [module, moduleId]
  );

  return (
    <>
      {isAllListPage ? (
        <Text as="h4" className="text-accent font-bold dark:text-white">
          Support Tickets ({totalCount})
        </Text>
      ) : (
        <SectionHeading text={`Support Tickets (${totalCount})`} />
      )}

      <div className={twMerge('mt-6 mb-2 flex items-end justify-between gap-2', isAllListPage && 'mb-4')}>
        <DebouncedInput
          containerClassName="sm:w-72"
          value={searching.search_str}
          onChange={(v) => searching.setSearch(String(v))}
          synchExternalValue={false}
          label="Search"
          focused={isAllListPage}
        />
        <FilterTrigger advFilters={advFiltersState} />
      </div>

      <BodyScrollVirtualizedList
        data={tickets}
        hasMore={hasMore}
        isLoading={isLoading}
        fetchMore={fetchMore}
        renderItem={renderItem}
        estimatedSize={100}
        emptyMessage="No Support Tickets found"
        overscan={3}
      />
    </>
  );
}
